/**
 * Condition Badge
 *
 * Displays a detected customer condition with confidence indicator.
 */

import { motion } from 'framer-motion'

interface ConditionBadgeProps {
  type: string
  confidence: number
  intensity?: number
  compact?: boolean
}

const conditionConfig: Record<string, { color: string; bgColor: string; icon: string; label: string }> = {
  stress: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: '😰',
    label: 'Stress',
  },
  frustration: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    icon: '😤',
    label: 'Frustration',
  },
  confusion: {
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    icon: '😕',
    label: 'Confusion',
  },
  satisfaction: {
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: '😊',
    label: 'Satisfaction',
  },
  urgency: {
    color: 'text-rose-700',
    bgColor: 'bg-rose-100',
    icon: '⏰',
    label: 'Urgency',
  },
  anxiety: {
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
    icon: '😟',
    label: 'Anxiety',
  },
  impatience: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: '😠',
    label: 'Impatience',
  },
  rapport: {
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: '🤝',
    label: 'Rapport',
  },
  disengagement: {
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: '😶',
    label: 'Disengagement',
  },
}

export default function ConditionBadge({ type, confidence, intensity, compact }: ConditionBadgeProps) {
  const config = conditionConfig[type] || {
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: '❓',
    label: type,
  }

  const confidenceLevel = confidence >= 0.8 ? 'high' : confidence >= 0.6 ? 'medium' : 'low'

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      >
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.bgColor}`}
    >
      <span className="text-lg">{config.icon}</span>
      <div className="flex flex-col">
        <span className={`text-sm font-semibold ${config.color}`}>
          {config.label}
        </span>
        <div className="flex items-center gap-2 text-xs">
          <span className={config.color}>
            {Math.round(confidence * 100)}% confidence
          </span>
          {intensity !== undefined && (
            <>
              <span className="text-gray-400">•</span>
              <span className={config.color}>
                {Math.round(intensity * 100)}% intensity
              </span>
            </>
          )}
        </div>
      </div>

      {/* Confidence indicator dots */}
      <div className="flex gap-0.5 ml-2">
        {[1, 2, 3].map((level) => (
          <span
            key={level}
            className={`w-1.5 h-1.5 rounded-full ${
              (confidenceLevel === 'high' && level <= 3) ||
              (confidenceLevel === 'medium' && level <= 2) ||
              (confidenceLevel === 'low' && level <= 1)
                ? config.bgColor.replace('100', '500')
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    </motion.div>
  )
}
